import axios from 'axios';
import { InternetPlan, CoverageArea, ServiceRequest, ContactForm, AvailabilityCheck } from '../types';

export const FALLBACK_PLANS: InternetPlan[] = [
  {
    id: 1,
    name: 'STARTER',
    speed: '100-300/50 Mbps',
    price: 39.99,
    features: ['Perfect for 1-2 people', 'HD streaming', 'Free installation', 'Wireless router included', 'Unlimited data'],
    popular: false,
    category: 'basic',
    taxesApply: true,
  },
  {
    id: 2,
    name: 'ADVANCED',
    speed: '100/50 Mbps',
    price: 59.99,
    features: ['Great for single family', '4K streaming', '1 gaming device', 'Free installation', 'Wireless router included', 'Unlimited data', 'Premium Router $10.00/month (Optional Add-on)', 'Static IP $5.00/month (Optional Add-on)'],
    popular: true,
    category: 'basic',
    taxesApply: true,
  },
  {
    id: 3,
    name: 'SUPREME',
    speed: '150/75 Mbps',
    price: 79.99,
    features: ['Great for small families', '4K streaming', '2 gaming devices', 'Free installation', 'Wireless router included', 'Unlimited data', 'Premium Router $10.00/month (Optional Add-on)', 'Static IP $5.00/month (Optional Add-on)'],
    popular: false,
    category: 'basic',
    taxesApply: true,
  },
  {
    id: 4,
    name: 'ULTIMATE',
    speed: '200+/75 Mbps',
    price: 99.99,
    features: ['Maximum performance', '4K streaming on multiple devices', 'Gaming ready', 'Free installation', 'Wireless router included', 'Unlimited data', 'Premium Router $10.00/month (Optional Add-on)', 'Static IP $5.00/month (Optional Add-on)'],
    popular: false,
    category: 'basic',
    taxesApply: true,
  },
  {
    id: 6,
    name: 'ULTRA',
    speed: '500/500 Mbps',
    price: 59.99,
    features: ['Great for single family', '4K streaming', 'Gaming ready', 'Free installation', 'Wireless router included', 'Unlimited data', 'Premium Router $10.00/month (Optional Add-on)', 'Static IP $5.00/month (Optional Add-on)'],
    popular: false,
    category: 'fiber',
    taxesApply: true,
  },
  {
    id: 7,
    name: 'PRIME',
    speed: '700/700 Mbps',
    price: 79.99,
    features: ['Great for families and work from home', '4K streaming on multiple devices', 'Gaming ready', 'Free installation', 'Wireless router included', 'Unlimited data', 'Premium Router $10.00/month (Optional Add-on)', 'Static IP $5.00/month (Optional Add-on)'],
    popular: true,
    category: 'fiber',
    taxesApply: true,
  },
  {
    id: 8,
    name: 'INFINITY',
    speed: '1G/1G',
    price: 99.99,
    features: ['Great for large families and work from home', '4K & 8K streaming', 'Competitive gaming', 'Free installation', 'Wireless router included', 'Unlimited data', 'Premium Router $10.00/month (Optional Add-on)', 'Static IP $5.00/month (Optional Add-on)'],
    popular: false,
    category: 'fiber',
    taxesApply: true,
  },
  {
    id: 9,
    name: 'Business PRO',
    speed: '2.5G/2.5G',
    price: 249.99,
    features: ['Business grade fiber', 'Dedicated support', 'Static IP included', 'Premium Router included', 'Free installation', '99.9% uptime', 'Unlimited data'],
    popular: false,
    category: 'business',
    taxesApply: true,
  },
  {
    id: 10,
    name: 'Bizz 500',
    speed: '500/500 Mbps',
    price: 99.99,
    features: [
      'Bizz Support — priority help from our Arizona business team',
      'Built for offices, POS, and cloud apps',
      'Free installation',
      'Wireless router included',
      'Unlimited data',
      'Static IP available',
    ],
    popular: false,
    category: 'business',
    taxesApply: true,
  },
  {
    id: 11,
    name: 'Bizz 1G',
    speed: '1G/1G',
    price: 149.99,
    features: [
      'Bizz Support — priority help from our Arizona business team',
      'Gigabit symmetrical speed for demanding businesses',
      'Free installation',
      'Wireless router included',
      'Unlimited data',
      'Static IP available',
    ],
    popular: true,
    category: 'business',
    taxesApply: true,
  },
];

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const apiService = {
  // Get internet plans
  getPlans: async (): Promise<InternetPlan[]> => {
    try {
      const response = await api.get('/plans');
      return response.data;
    } catch (error) {
      console.warn('Plans API unavailable, using fallback plans.', error);
      return FALLBACK_PLANS;
    }
  },

  // Check service availability
  checkAvailability: async (data: AvailabilityCheck) => {
    const response = await api.post('/check-availability', data);
    return response.data;
  },

  // Submit service request
  submitServiceRequest: async (data: ServiceRequest) => {
    const response = await api.post('/service-request', data);
    return response.data;
  },

  // Submit contact form
  submitContactForm: async (data: ContactForm) => {
    const response = await api.post('/contact', data);
    return response.data;
  },

  // Get coverage areas
  getCoverageAreas: async (): Promise<CoverageArea[]> => {
    const response = await api.get('/coverage');
    return response.data;
  },

  // Health check
  healthCheck: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};
